/**
 * Peças de "Instalar plugin…" que o cadastro de plugins oficiais (Administração) também usa: o ícone
 * de um plugin, o resumo de um pacote conferido, as abas Desk Cloud e Computador e as abas em si
 * (docs/spec/web-plugins.md, "Como se usa" e "Plugins oficiais").
 */
import { useEffect, useState, type DragEvent as ReactDragEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { CloudPickBrowser } from '../../cloud/CloudFilePicker.js';
import { useListen } from '../../cloud/listen.js';
import { useCloudListing } from '../../cloud/useCloudListing.js';
import { useLanguage, useT } from '../../i18n/index.js';
import { serverUrl } from '../../serverUrl.js';
import type { DeskFile } from '../../shared.js';
import { Icon } from '../../ui/icons.js';
import { coarsePointer } from '../../ui/touch.js';
import { formatReport, langOf, localize, type Inspection, type Issue, type Localized } from './sdk.js';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** O ícone de um plugin: o do pacote, ou o glifo de ficha (ou de tabela) num quadrado apagado. */
export function PluginIcon({ url, table = false, size = 40 }: { url: string | null | undefined; table?: boolean; size?: number }) {
  // o endereço que não carregou: outro endereço (versão nova) tenta de novo
  const [failed, setFailed] = useState<string | null>(null);
  return (
    <span className="rd-pluginIcon" style={{ width: size, height: size }} aria-hidden="true">
      {url && failed !== url ? <img src={serverUrl(url)} alt="" draggable={false} onError={() => setFailed(url)} /> : <Icon name={table ? 'grid' : 'sheet'} size={Math.round(size / 2)} />}
    </span>
  );
}

/** O endereço do ícone de um plugin da mesa: a pasta do pacote mais o caminho do manifesto. */
export function roomPluginIcon(web: { baseUrl: string; icon?: string | undefined }): string | null {
  return web.icon && web.baseUrl ? `${web.baseUrl}${web.icon.split('/').map(encodeURIComponent).join('/')}` : null;
}

/** O que o plugin traz, curto: "Ficha", "2 fichas · Tabela da mesa". */
export function pluginKinds(t: Translate, sheets: number, tables: number): string {
  const parts = [
    ...(sheets ? [sheets === 1 ? t('pluginInstall.kind.sheet') : t('pluginInstall.kind.sheets', { n: sheets })] : []),
    ...(tables ? [tables === 1 ? t('pluginInstall.kind.table') : t('pluginInstall.kind.tables', { n: tables })] : []),
  ];
  return parts.join(' · ');
}

/** "1.1.2 · por RPG Desk · Ficha": a linha de baixo do nome. */
export function pluginMeta(t: Translate, plugin: { version: string; author?: string | undefined; sheets: unknown[]; tables: unknown[] }): string {
  return [plugin.version, ...(plugin.author ? [t('webInstall.by', { author: plugin.author })] : []), pluginKinds(t, plugin.sheets.length, plugin.tables.length)].filter(Boolean).join(' · ');
}

export function IssueList({ issues }: { issues: Issue[] }) {
  const t = useT();
  if (issues.length === 0) return null;
  const sorted = [...issues.filter((i) => i.level === 'error'), ...issues.filter((i) => i.level === 'warning')];
  return (
    <ul className="rd-webinstall__issues" aria-label={t('webInstall.issues')}>
      {sorted.map((issue, i) => (
        <li key={i} className={`rd-webinstall__issue rd-webinstall__issue--${issue.level}`}>
          <strong>{t(issue.level === 'error' ? 'webInstall.error' : 'webInstall.warning')}</strong>
          {issue.file ? <code>{`${issue.file}${issue.line ? `:${issue.line}` : ''}`}</code> : null} {issue.message}
          {issue.fix ? <div className="rd-webinstall__fix">{t('webInstall.fix', { fix: issue.fix })}</div> : null}
        </li>
      ))}
    </ul>
  );
}

/** O relatório do verificador, para "Copiar relatório" (vai para a IA na língua da interface). */
export function inspectionReport(inspection: Inspection, fileName: string, language: string): string {
  const m = inspection.manifest;
  return formatReport(inspection.issues, m ? `${localize(m.name, language)} ${m.version}` : fileName, langOf(language));
}

/**
 * O resumo de um pacote conferido: nome, versão e autor, o aviso de versão de quem chama (`notice`), a
 * descrição, as fichas e tabelas, os webhooks e o relatório. `children` entra antes do relatório (os
 * sistemas, no cadastro de um oficial).
 */
export function PluginSummary({ inspection, fileName, notice, children }: { inspection: Inspection; fileName: string; notice?: ReactNode; children?: ReactNode }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const m = inspection.manifest;
  const errors = inspection.issues.filter((i) => i.level === 'error');
  const title = (text: Localized) => localize(text, language);
  return (
    <div className="rd-webinstall">
      {m ? (
        <>
          <p className="rd-webinstall__name">
            <strong>{title(m.name)}</strong> <span>{m.version}</span>
            {m.author ? <span> · {t('webInstall.by', { author: m.author })}</span> : null}
          </p>
          {notice}
          {m.description ? <p className="rd-webinstall__hint">{title(m.description)}</p> : null}
          <ul className="rd-webinstall__types">
            {m.sheets.map((d) => (
              <li key={d.type}>{t('webInstall.sheet', { title: title(d.title) })}</li>
            ))}
            {m.tables.map((d) => (
              <li key={d.type}>{t('webInstall.table', { title: title(d.title) })}</li>
            ))}
          </ul>
          {m.webhooks.length ? (
            <div className="rd-webinstall__hooks">
              <p>{t('webInstall.webhooks')}</p>
              <ul>
                {m.webhooks.map((h) => (
                  <li key={h.name}>
                    <code>{h.name}</code> {h.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <p>{t('webInstall.unreadable', { file: fileName })}</p>
      )}
      {children}
      {errors.length ? <p className="rd-webinstall__warn">{t('webInstall.hasErrors')}</p> : null}
      <IssueList issues={inspection.issues} />
    </div>
  );
}

/**
 * As abas sublinhadas de um diálogo (DESIGN.md, "Abas sublinhadas"), com as setas do teclado. Cada
 * aba controla o painel `<idBase>-<aba>`.
 */
export function DialogTabs<T extends string>({ tabs, value, onChange, label, idBase }: { tabs: { key: T; label: string }[]; value: T; onChange: (tab: T) => void; label: string; idBase: string }) {
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const i = tabs.findIndex((x) => x.key === value);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]!;
    onChange(next.key);
    (e.currentTarget.querySelector(`[data-tab="${next.key}"]`) as HTMLElement | null)?.focus();
  };
  return (
    <div className="rd-imgpick__tabs rd-pluginTabs" role="tablist" aria-label={label} onKeyDown={onKey}>
      {tabs.map((x) => (
        <button
          key={x.key}
          type="button"
          role="tab"
          id={`${idBase}-tab-${x.key}`}
          data-tab={x.key}
          aria-selected={value === x.key}
          aria-controls={`${idBase}-${x.key}`}
          tabIndex={value === x.key ? 0 : -1}
          className={value === x.key ? 'is-on' : ''}
          onClick={() => onChange(x.key)}
        >
          {x.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A aba Desk Cloud: os pacotes de plugin (.zip e .html) do Desk Cloud de quem usa, com as pastas e a
 * busca; escolhe-se um (o clique troca a escolha, dois cliques seguem).
 */
export function CloudPluginPanel({ chosen, onChoose, onPick, hint }: { chosen: DeskFile | null; onChoose: (file: DeskFile | null) => void; onPick: (file: DeskFile) => void; hint: string }) {
  const t = useT();
  const listing = useCloudListing({ plugins: true });
  // o áudio que se ouviu no seletor para quando a aba sai
  useEffect(() => () => useListen.getState().stop(), []);
  const map = new Map(chosen ? [[chosen.id, chosen]] : []);
  return (
    <CloudPickBrowser
      listing={listing}
      chosen={map}
      onToggle={(file) => onChoose(chosen?.id === file.id ? null : file)}
      onPick={onPick}
      empty={t('pluginInstall.cloud.empty')}
      hint={hint}
      // o nome do cartão já traz a extensão: o tipo diz só que é um pacote (curto, para o tamanho caber)
      kindLabel={() => t('pluginInstall.cloud.package')}
    />
  );
}

/** Um arquivo arrastado (e não um texto ou um item da mesa). */
const draggingFiles = (e: ReactDragEvent) => [...e.dataTransfer.types].includes('Files');

/**
 * A aba Computador: soltar o .zip ou o .html na área, ou escolher. A área tem fio em repouso e fica
 * tracejada e tingida enquanto um arquivo passa por cima (DESIGN.md: o tracejado é do alvo de soltar).
 */
export function ComputerPluginPanel({ onFile, busy, hint }: { onFile: (file: File) => void; busy: boolean; hint: string }) {
  const t = useT();
  const [over, setOver] = useState(false);
  return (
    <div
      className={`rd-pluginDrop${over ? ' is-over' : ''}`}
      onDragOver={(e) => {
        if (!draggingFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => {
        if (!draggingFiles(e)) return;
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (file && !busy) onFile(file);
      }}
    >
      <span className="rd-pluginDrop__icon" aria-hidden="true">
        <Icon name="upload" size={22} />
      </span>
      {/* no toque não há o que soltar: a área só convida a escolher */}
      <p className="rd-pluginDrop__title">{t(coarsePointer() ? 'pluginInstall.computer.pick' : 'pluginInstall.computer.drop')}</p>
      <label className={`rd-btn rd-btn--secondary rd-filePick${busy ? ' is-busy' : ''}`} aria-disabled={busy || undefined}>
        {busy ? t('pluginInstall.reading') : t('pluginInstall.computer.choose')}
        <input
          type="file"
          accept=".html,.htm,.zip"
          hidden
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onFile(file);
          }}
        />
      </label>
      <p className="rd-pluginDrop__hint">{hint}</p>
    </div>
  );
}
